import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import ServiceModal from './ServiceModal';
import { supabase } from '../../../lib/supabaseClient';

interface Service {
  id: string;
  name: string;
  icon: string;
  available?: boolean;
}

interface HeroProps {
  cityName?: string;
  cityHeadline?: string;
  citySubheadline?: string;
}

const services: Service[] = [
  { id: 'handyman', name: 'Handyman', icon: 'ri-tools-fill' },
  { id: 'plumber', name: 'Plumber', icon: 'ri-drop-fill' },
  { id: 'electrician', name: 'Electrician', icon: 'ri-flashlight-fill' },
  { id: 'cleaner', name: 'Cleaner', icon: 'ri-brush-fill' },
  { id: 'waste-removal', name: 'Waste Removal', icon: 'ri-delete-bin-fill' },
  { id: 'transportation', name: 'Transportation', icon: 'ri-truck-fill' },
];

export default function Hero({ cityName, cityHeadline, citySubheadline }: HeroProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [postcodeValid, setPostcodeValid] = useState(false);
  const [postcodeError, setPostcodeError] = useState('');
  const [error, setError] = useState('');
  const [servicesList, setServices] = useState<Service[]>([
    { id: 'handyman', name: 'Handyman', icon: 'ri-tools-fill', available: false },
    { id: 'plumber', name: 'Plumber', icon: 'ri-drop-fill', available: false },
    { id: 'electrician', name: 'Electrician', icon: 'ri-flashlight-fill', available: false },
    { id: 'cleaner', name: 'Cleaner', icon: 'ri-brush-fill', available: false },
    { id: 'waste-removal', name: 'Waste Removal', icon: 'ri-delete-bin-fill', available: false },
    { id: 'transportation', name: 'Transportation', icon: 'ri-truck-fill', available: false },
  ]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const categoryGridRef = useRef<HTMLDivElement>(null);

  const [startPostcode, setStartPostcode] = useState('');
  const [finishPostcode, setFinishPostcode] = useState('');
  const [validationError, setValidationError] = useState('');
  const [startValidationError, setStartValidationError] = useState('');
  const [finishValidationError, setFinishValidationError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [startIsValid, setStartIsValid] = useState(false);
  const [finishIsValid, setFinishIsValid] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Determine if we're on a city page
  const isCityPage = cityName !== undefined;

  // Default homepage text
  const defaultHeadline = "Book trusted local services — fast";
  const defaultSubheadline = "Reliable handymen, cleaners, plumbers and more – just a few taps away!";
  const defaultSupportingLine = "From handyman and plumbing to cleaning, waste removal, and transportation — all priced upfront with no quotes or hidden fees.";

  // Use city-specific text if provided, otherwise use defaults
  const displayHeadline = cityHeadline || defaultHeadline;
  const displaySubheadline = citySubheadline || defaultSubheadline;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (postcode.length >= 5) {
        validatePostcode(postcode);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [postcode]);

  const validatePostcode = async (code: string, type: 'main' | 'start' | 'finish' = 'main') => {
    if (!code.trim()) {
      if (type === 'start') {
        setStartValidationError('');
        setStartIsValid(false);
      } else if (type === 'finish') {
        setFinishValidationError('');
        setFinishIsValid(false);
      } else {
        setValidationError('');
        setIsValid(false);
      }
      return;
    }

    setIsValidating(true);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: code }),
        }
      );

      const data = await response.json();

      if (data.valid) {
        if (type === 'start') {
          setStartValidationError('');
          setStartIsValid(true);
        } else if (type === 'finish') {
          setFinishValidationError('');
          setFinishIsValid(true);
        } else {
          setValidationError('');
          setIsValid(true);
          setPostcodeValid(true);
          setPostcodeError('');
        }
      } else {
        if (type === 'start') {
          setStartValidationError(data.message || 'Invalid postcode');
          setStartIsValid(false);
        } else if (type === 'finish') {
          setFinishValidationError(data.message || 'Invalid postcode');
          setFinishIsValid(false);
        } else {
          setValidationError(data.message || 'Invalid postcode');
          setIsValid(false);
          setPostcodeValid(false);
          setPostcodeError(data.message || 'Invalid postcode');
        }
      }
    } catch (error) {
      if (type === 'start') {
        setStartValidationError('Unable to validate postcode');
        setStartIsValid(false);
      } else if (type === 'finish') {
        setFinishValidationError('Unable to validate postcode');
        setFinishIsValid(false);
      } else {
        setValidationError('Unable to validate postcode');
        setIsValid(false);
        setPostcodeValid(false);
        setPostcodeError('Unable to validate postcode');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const calculateTransportationPrice = async () => {
    if (!startIsValid || !finishIsValid) return;

    setIsCalculating(true);
    
    try {
      // Get coordinates for both postcodes
      const [startResponse, finishResponse] = await Promise.all([
        fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ postcode: startPostcode }),
          }
        ),
        fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ postcode: finishPostcode }),
          }
        )
      ]);

      const startData = await startResponse.json();
      const finishData = await finishResponse.json();

      if (startData.valid && finishData.valid && startData.latitude && finishData.latitude) {
        // Calculate distance in miles
        const distance = calculateDistance(
          startData.latitude,
          startData.longitude,
          finishData.latitude,
          finishData.longitude
        );

        // Price calculation: £2 per mile + £50 platform fee
        const baseCost = distance * 2;
        const platformFee = 50;
        const subtotal = baseCost + platformFee;
        const vat = subtotal * 0.20; // 20% VAT
        const total = subtotal + vat;

        setCalculatedPrice(Math.round(total * 100) / 100);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    if (selectedService === 'transportation' && startIsValid && finishIsValid) {
      calculateTransportationPrice();
    } else {
      setCalculatedPrice(null);
    }
  }, [selectedService, startIsValid, finishIsValid, startPostcode, finishPostcode]);

  const handleBookNow = () => {
    if (selectedService === 'transportation') {
      if (startIsValid && finishIsValid && calculatedPrice) {
        navigate(`/service-request?service=${encodeURIComponent(selectedService)}&startPostcode=${encodeURIComponent(startPostcode)}&finishPostcode=${encodeURIComponent(finishPostcode)}&price=${calculatedPrice}`);
      }
    } else {
      if (isValid && selectedService) {
        navigate(`/service-request?service=${encodeURIComponent(selectedService)}&postcode=${encodeURIComponent(postcode)}`);
      }
    }
  };

  const handlePostcodeSubmit = () => {
    if (postcode.length >= 5) {
      validatePostcode(postcode);
    } else {
      setError('Please enter a valid postcode');
    }
  };

  const handlePostcodeBlur = () => {
    if (postcode.length >= 5 && !postcodeValid) {
      validatePostcode(postcode);
    }
  };

  // Handle continue button - navigate to service-request page
  const handleContinue = () => {
    if (postcodeValid && selectedCategory) {
      navigate(`/service-request?postcode=${encodeURIComponent(postcode)}&category=${selectedCategory}`);
    }
  };

  const canContinue = postcodeValid && selectedCategory !== null;

  // Cardiff-specific background
  const isCardiff = cityName === 'Cardiff';
  const heroBackgroundImage = isCardiff 
    ? 'https://public.readdy.ai/ai/img_res/716f0ce64f9071a9d2ba5d8f6bdf95ab.jpg'
    : 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20interior%20with%20clean%20lines%20and%20natural%20light%2C%20Scandinavian%20design%20aesthetic%2C%20bright%20airy%20living%20space%20with%20wooden%20floors%2C%20contemporary%20furniture%2C%20plants%2C%20warm%20welcoming%20atmosphere%2C%20professional%20interior%20photography%2C%20soft%20shadows%2C%20neutral%20color%20palette&width=1920&height=1080&seq=hero-bg-001&orientation=landscape';

  // Cardiff mobile background
  const heroBackgroundImageMobile = isCardiff
    ? 'https://readdy.ai/api/search-image?query=Cardiff%20Castle%20at%20twilight%20with%20dramatic%20dark%20moody%20atmosphere%2C%20Welsh%20historic%20landmark%20with%20illuminated%20stone%20walls%2C%20evening%20sky%20with%20deep%20blue%20and%20purple%20tones%2C%20atmospheric%20lighting%2C%20vertical%20composition%20for%20mobile%2C%20professional%20architectural%20photography&width=800&height=1200&seq=cardiff-hero-mobile-dark-001&orientation=portrait'
    : 'https://readdy.ai/api/search-image?query=professional%20skilled%20tradespeople%20team%20portrait%20featuring%20male%20carpenter%20holding%20wooden%20boards%20and%20hand%20saw%20female%20electrician%20with%20voltage%20tester%20and%20wire%20tools%20male%20plumber%20with%20pipe%20wrench%20and%20copper%20pipes%20female%20painter%20with%20paint%20roller%20and%20brush%20all%20wearing%20casual%20work%20attire%20tool%20belts%20safety%20glasses%20standing%20confidently%20in%20bright%20modern%20workshop%20with%20natural%20sunlight%20wooden%20workbench%20background%20tools%20hanging%20on%20wall%20warm%20inviting%20atmosphere%20high%20quality%20professional%20photography&width=800&height=600&seq=tradespeople-portrait-mobile-2025&orientation=landscape';

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Navigation Bar */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white'
        }`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/e0dadf219d3b341a9184e92001cfe9b6.png" 
                  alt="Taskbudy" 
                  className="h-10 lg:h-12 w-auto"
                />
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-8">
                <Link to="/" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Home
                </Link>
                <Link to="/services" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Services
                </Link>
                <Link to="/locations" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Locations
                </Link>
                <Link to="/pricing" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Pricing
                </Link>
                <Link to="/about" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  About
                </Link>
                <Link to="/contact" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Contact
                </Link>
                <Link to="/auth/signin" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Sign In
                </Link>
                <Link to="/auth/signup" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Sign Up
                </Link>
                <Link to="/auth/provider-login" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                  Provider Login
                </Link>
                <Link
                  to="/provider-signup"
                  className="bg-[#fe7a14] hover:bg-[#e56a0f] text-white px-6 py-3 rounded-xl font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  Become a Provider
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden text-[#0F2A44] w-12 h-12 flex items-center justify-center cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="lg:hidden py-6 border-t border-gray-200 bg-white">
                <div className="flex flex-col space-y-2">
                  <Link
                    to="/"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Home
                  </Link>
                  <Link
                    to="/services"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Services
                  </Link>
                  <Link
                    to="/locations"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Locations
                  </Link>
                  <Link
                    to="/pricing"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/about"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    About
                  </Link>
                  <Link
                    to="/contact"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link
                    to="/auth/signin"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/auth/signup"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                  <Link
                    to="/auth/provider-login"
                    className="text-[#0F2A44] hover:text-[#fe7a14] hover:bg-orange-50 font-medium transition-all whitespace-nowrap px-4 py-4 rounded-lg cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Provider Login
                  </Link>
                  <Link
                    to="/provider-signup"
                    className="bg-[#fe7a14] hover:bg-[#e56a0f] text-white px-6 py-4 rounded-xl font-semibold transition-all text-center whitespace-nowrap cursor-pointer mt-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Become a Provider
                  </Link>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Version */}
        <div className="lg:hidden w-full">
          {/* Hero Section - Adjusted height */}
          <div className="relative w-full min-h-fit">
            {/* Hero Text - Centered with better spacing */}
            <div className="relative z-10 px-4 flex flex-col items-center text-center pt-20 pb-4 bg-white">
              {/* Minimal Elegant Badge */}
              <div className="inline-flex items-center gap-1.5 mb-2">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                <span className="text-[10px] font-medium text-gray-500 tracking-wide">
                  {isCityPage ? `Available 24/7 in ${cityName}` : 'Available 24/7 in your area'}
                </span>
              </div>

              {/* Clean Modern Heading */}
              <h1 className="text-xl font-bold leading-tight mb-2 tracking-tight">
                <span className="text-gray-900">Professional Home</span>
                <br />
                <span className="text-gray-900">Services </span>
                {isCityPage ? (
                  <span className="relative inline-block">
                    <span className="text-gray-900">in </span>
                    <span className="text-[#ff751f]">{cityName}</span>
                    <svg className="absolute -bottom-0.5 left-0 w-full" viewBox="0 0 100 12" preserveAspectRatio="none">
                      <path d="M0,8 Q25,0 50,8 T100,8" stroke="#ff751f" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.4"/>
                    </svg>
                  </span>
                ) : (
                  <span className="relative inline-block">
                    <span className="text-gray-900">Done </span>
                    <span className="text-[#ff751f]">Right</span>
                    <svg className="absolute -bottom-0.5 left-0 w-full" viewBox="0 0 100 12" preserveAspectRatio="none">
                      <path d="M0,8 Q25,0 50,8 T100,8" stroke="#ff751f" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.4"/>
                    </svg>
                  </span>
                )}
              </h1>

              {/* Refined Subheading */}
              <p className="text-gray-500 text-xs mb-3 leading-relaxed max-w-[280px] font-normal">
                {isCityPage ? displaySubheadline : 'Vetted professionals for every task. Transparent pricing. No surprises.'}
              </p>

              {/* Minimal Trust Indicators - Mobile optimized - Compact layout */}
              <div className="flex items-center justify-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <i className="ri-shield-check-fill text-[9px] text-white"></i>
                  </div>
                  <span className="font-medium text-gray-900 text-[10px]">Verified</span>
                </div>

                <div className="w-px h-4 bg-gray-200"></div>

                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-md bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
                    <i className="ri-time-fill text-[9px] text-white"></i>
                  </div>
                  <span className="font-medium text-gray-900 text-[10px]">Same Day</span>
                </div>

                <div className="w-px h-4 bg-gray-200"></div>

                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-md bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <i className="ri-price-tag-3-fill text-[9px] text-white"></i>
                  </div>
                  <span className="font-medium text-gray-900 text-[10px]">Fixed Price</span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Module - Positioned just under text */}
          <div className="relative z-20 px-4 pb-8 pt-2 bg-white">
            <div className="rounded-2xl p-4 max-w-md mx-auto relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-[0_25px_80px_-15px_rgba(0,0,0,0.4)]">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-[#ff751f]/20 to-transparent rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full blur-xl"></div>
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff751f] via-orange-400 to-amber-400"></div>
              
              <div className="relative z-10 pt-1">
                {/* Header */}
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-[#ff751f] to-orange-600 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/30 ring-2 ring-orange-500/20">
                    <i className="ri-calendar-check-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <h2 className="text-white text-lg font-bold tracking-tight">Book a Service</h2>
                    <p className="text-gray-400 text-[10px]">Get started in 2 simple steps</p>
                  </div>
                </div>

                {/* Step 1: Postcode */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 bg-gradient-to-br from-[#ff751f] to-orange-600 shadow-lg shadow-orange-500/30 ring-2 ring-orange-500/20 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                    <label className="text-white text-sm font-semibold">Enter your postcode</label>
                  </div>
                  <div className="relative group">
                    <input
                      type="text"
                      value={postcode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setPostcode(value);
                        if (postcodeError) setPostcodeError('');
                      }}
                      onBlur={handlePostcodeBlur}
                      placeholder="e.g., CF10 1AA"
                      className="w-full px-3 py-2.5 rounded-lg border-2 focus:outline-none font-medium transition-all text-sm bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder-gray-400 focus:border-[#ff751f] focus:bg-white/15 hover:border-white/30"
                    />
                    <i className="ri-map-pin-2-fill absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-base pointer-events-none group-focus-within:text-[#ff751f] transition-colors"></i>
                  </div>
                  {postcodeError && (
                    <p className="text-xs mt-1.5 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-red-400 bg-red-500/10 border-red-500/20">
                      <i className="ri-error-warning-fill text-sm"></i>
                      {postcodeError}
                    </p>
                  )}
                  {isValidating && (
                    <p className="text-xs mt-1.5 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-orange-400 bg-orange-500/10 border-orange-500/20">
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                      Validating postcode...
                    </p>
                  )}
                  {postcodeValid && (
                    <p className="text-xs mt-1.5 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-emerald-400 bg-emerald-500/10 border-emerald-500/20">
                      <i className="ri-checkbox-circle-fill text-sm"></i>
                      Postcode verified — we cover your area!
                    </p>
                  )}
                </div>

                {/* Step 2: Service Category */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      postcodeValid
                        ? 'bg-gradient-to-br from-[#ff751f] to-orange-600 text-white shadow-lg shadow-orange-500/30 ring-2 ring-orange-500/20'
                        : 'bg-gray-700 text-gray-500 ring-2 ring-gray-600'
                    }`}>2</span>
                    <label className={`text-sm font-semibold transition-colors ${
                      postcodeValid ? 'text-white' : 'text-gray-500'
                    }`}>Select a category</label>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {servicesList.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => postcodeValid && setSelectedCategory(cat.id)}
                        disabled={!postcodeValid}
                        className={`aspect-square p-1.5 rounded-lg border-2 transition-all flex flex-col items-center justify-center relative overflow-hidden ${
                          !postcodeValid
                            ? 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed'
                            : selectedCategory === cat.id
                            ? 'border-[#ff751f] bg-[#ff751f]/15 shadow-md scale-[1.02]'
                            : 'border-white/15 bg-white/10 hover:border-[#ff751f]/50 hover:bg-white/15 hover:scale-[1.02]'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-1 transition-all ${
                          selectedCategory === cat.id 
                            ? 'bg-gradient-to-br from-[#ff751f] to-orange-500 shadow-md' 
                            : 'bg-white/15'
                        }`}>
                          <i className={`${cat.icon} text-base ${
                            selectedCategory === cat.id
                              ? 'text-white'
                              : 'text-gray-300'
                          }`}></i>
                        </div>
                        <span className={`text-[9px] font-medium text-center leading-tight ${
                          selectedCategory === cat.id
                            ? 'text-[#ff751f]'
                            : 'text-gray-300'
                        }`}>
                          {cat.name}
                        </span>
                        {selectedCategory === cat.id && (
                          <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 bg-[#ff751f] rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-[8px]"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleContinue}
                  disabled={!postcode || !selectedCategory || isValidating}
                  className="w-full bg-gradient-to-r from-[#ff751f] to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white font-bold py-3 px-5 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap hover:scale-[1.01] active:scale-[0.99] disabled:hover:scale-100 shadow-lg shadow-orange-500/40 hover:shadow-xl hover:shadow-orange-500/50 text-sm"
                >
                  {isValidating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Validating...
                    </>
                  ) : !postcodeValid || !selectedCategory ? (
                    <>
                      <i className="ri-lock-line"></i>
                      Complete steps above
                    </>
                  ) : (
                    <>
                      Continue to booking
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>

                {/* Trust badges */}
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="flex items-center justify-center gap-3 text-[10px] text-gray-400">
                    <div className="flex items-center gap-1">
                      <i className="ri-shield-check-fill text-emerald-400 text-sm"></i>
                      <span className="font-medium">Verified Pros</span>
                    </div>
                    <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                    <div className="flex items-center gap-1">
                      <i className="ri-price-tag-3-fill text-[#ff751f] text-sm"></i>
                      <span className="font-medium">Fixed Pricing</span>
                    </div>
                    <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-fill text-sky-400 text-sm"></i>
                      <span className="font-medium">Same Day</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Version */}
        <div className="hidden lg:block w-full">
          {/* Hero Section with Background - Full Screen */}
          <div className="relative w-full h-screen">
            {/* Enhanced White Background with Decorative Elements */}
            <div className="absolute inset-0 w-full h-full bg-white overflow-hidden">
              {/* Subtle grid pattern */}
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
              
              {/* Large gradient blur - top right */}
              <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-gradient-to-br from-orange-200/40 via-amber-100/30 to-transparent rounded-full blur-3xl"></div>
              
              {/* Medium gradient blur - bottom left */}
              <div className="absolute -bottom-48 -left-48 w-[500px] h-[500px] bg-gradient-to-tr from-teal-100/30 via-emerald-50/20 to-transparent rounded-full blur-3xl"></div>
              
              {/* Small accent blur - center right */}
              <div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] bg-gradient-to-bl from-orange-100/25 to-transparent rounded-full blur-2xl transform -translate-y-1/2"></div>
              
              {/* Decorative circles */}
              <div className="absolute top-20 left-[15%] w-64 h-64 border border-gray-100 rounded-full"></div>
              <div className="absolute top-32 left-[18%] w-48 h-48 border border-gray-100/80 rounded-full"></div>
              <div className="absolute bottom-32 right-[10%] w-80 h-80 border border-orange-100/50 rounded-full"></div>
              
              {/* Floating dots pattern */}
              <div className="absolute top-40 right-[20%] w-2 h-2 bg-orange-300/40 rounded-full"></div>
              <div className="absolute top-52 right-[25%] w-1.5 h-1.5 bg-teal-300/40 rounded-full"></div>
              <div className="absolute top-36 right-[15%] w-1 h-1 bg-orange-400/30 rounded-full"></div>
              <div className="absolute bottom-40 left-[25%] w-2 h-2 bg-emerald-300/30 rounded-full"></div>
              <div className="absolute bottom-52 left-[30%] w-1.5 h-1.5 bg-orange-300/40 rounded-full"></div>
              
              {/* Subtle diagonal lines */}
              <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] -right-[10%] w-[800px] h-[1px] bg-gradient-to-r from-transparent via-gray-200/50 to-transparent transform rotate-[30deg]"></div>
                <div className="absolute top-[30%] -right-[5%] w-[600px] h-[1px] bg-gradient-to-r from-transparent via-orange-200/30 to-transparent transform rotate-[30deg]"></div>
                <div className="absolute bottom-[20%] -left-[10%] w-[700px] h-[1px] bg-gradient-to-r from-transparent via-gray-200/40 to-transparent transform rotate-[-20deg]"></div>
              </div>
              
              {/* Corner accent shapes */}
              <div className="absolute top-0 right-0 w-[400px] h-[400px] overflow-hidden">
                <div className="absolute -top-48 -right-48 w-96 h-96 border-[40px] border-orange-50/60 rounded-full"></div>
              </div>
              <div className="absolute bottom-0 left-0 w-[300px] h-[300px] overflow-hidden">
                <div className="absolute -bottom-36 -left-36 w-72 h-72 border-[30px] border-teal-50/40 rounded-full"></div>
              </div>
            </div>

            {/* Content Container */}
            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-6xl mx-auto px-12 w-full">
                <div className="grid grid-cols-2 gap-16 items-center">
                  {/* Left: Hero Text */}
                  <div className="pl-4">
                    {/* Minimal Elegant Badge */}
                    <div className="inline-flex items-center gap-2.5 mb-8">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                      </span>
                      <span className="text-sm font-medium text-gray-600 tracking-wide">
                        {isCityPage ? `Available 24/7 in ${cityName}` : 'Available 24/7 in your area'}
                      </span>
                    </div>

                    {/* Clean Modern Heading */}
                    <h1 className="text-5xl font-bold leading-tight mb-6 tracking-tight">
                      <span className="text-gray-900">Professional Home</span>
                      <br />
                      <span className="text-gray-900">Services </span>
                      {isCityPage ? (
                        <span className="relative inline-block">
                          <span className="text-gray-900">in </span>
                          <span className="text-[#ff751f]">{cityName}</span>
                          <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 100 12" preserveAspectRatio="none">
                            <path d="M0,8 Q25,0 50,8 T100,8" stroke="#ff751f" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.4"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="relative inline-block">
                          <span className="text-gray-900">Done </span>
                          <span className="text-[#ff751f]">Right</span>
                          <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 100 12" preserveAspectRatio="none">
                            <path d="M0,8 Q25,0 50,8 T100,8" stroke="#ff751f" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.4"/>
                          </svg>
                        </span>
                      )}
                    </h1>

                    {/* Refined Subheading */}
                    <p className="text-gray-500 text-lg mb-10 leading-relaxed max-w-md font-normal">
                      {isCityPage ? displaySubheadline : 'Vetted professionals for every task. Transparent pricing. No surprises.'}
                    </p>

                    {/* Minimal Trust Indicators */}
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2.5 group cursor-pointer">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25 group-hover:shadow-xl group-hover:shadow-emerald-500/40 transition-all duration-300 group-hover:scale-105">
                            <i className="ri-shield-check-fill text-lg text-white"></i>
                          </div>
                          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm leading-tight">Verified</div>
                          <div className="text-xs text-gray-500 leading-tight">ID checked</div>
                        </div>
                      </div>

                      <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>

                      <div className="flex items-center gap-2.5 group cursor-pointer">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:shadow-xl group-hover:shadow-amber-500/40 transition-all duration-300 group-hover:scale-105">
                          <i className="ri-time-fill text-lg text-white"></i>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm leading-tight">Same Day</div>
                          <div className="text-xs text-gray-500 leading-tight">Book today</div>
                        </div>
                      </div>

                      <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>

                      <div className="flex items-center gap-2.5 group cursor-pointer">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/25 group-hover:shadow-xl group-hover:shadow-teal-500/40 transition-all duration-300 group-hover:scale-105">
                          <i className="ri-price-tag-3-fill text-lg text-white"></i>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm leading-tight">Fixed Price</div>
                          <div className="text-xs text-gray-500 leading-tight">No surprises</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Booking Module - Dark design */}
                  <div className="pr-4 mt-32">
                    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 shadow-[0_25px_80px_-15px_rgba(0,0,0,0.4)] max-w-md relative overflow-hidden">
                      {/* Decorative elements */}
                      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-[#ff751f]/20 to-transparent rounded-full blur-2xl"></div>
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full blur-xl"></div>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#ff751f] via-orange-400 to-amber-400"></div>
                      
                      <div className="relative z-10">
                        {/* Header */}
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex items-center gap-2 group">
                            <div className="relative">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
                                <i className="ri-shield-check-fill text-sm text-white"></i>
                              </div>
                              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white"></div>
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-xs leading-tight">Verified</div>
                              <div className="text-[10px] text-gray-500 leading-tight">ID checked</div>
                            </div>
                          </div>

                          <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>

                          <div className="flex items-center gap-2 group">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                              <i className="ri-time-fill text-sm text-white"></i>
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-xs leading-tight">Same Day</div>
                              <div className="text-[10px] text-gray-500 leading-tight">Book today</div>
                            </div>
                          </div>

                          <div className="w-px h-12 bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>

                          <div className="flex items-center gap-2 group">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/25">
                              <i className="ri-price-tag-3-fill text-sm text-white"></i>
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-xs leading-tight">Fixed Price</div>
                              <div className="text-[10px] text-gray-500 leading-tight">No surprises</div>
                            </div>
                          </div>
                        </div>

                        {/* Step 1: Postcode */}
                        <div className="mb-6">
                          <div className="flex items-center gap-2.5 mb-3">
                            <span className="w-7 h-7 bg-gradient-to-br from-[#ff751f] to-orange-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-orange-500/30 ring-2 ring-orange-500/20">1</span>
                            <label className="text-white text-sm font-semibold">Enter your postcode</label>
                          </div>
                          <div className="relative group">
                            <input
                              type="text"
                              value={postcode}
                              onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                setPostcode(value);
                                if (postcodeError) setPostcodeError('');
                              }}
                              onBlur={handlePostcodeBlur}
                              placeholder="e.g. SW1A 1AA"
                              className="w-full px-4 py-4 pr-12 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-[#ff751f] focus:bg-white/15 transition-all duration-200 text-base font-medium cursor-pointer hover:border-white/30"
                            />
                            <i className="ri-map-pin-2-fill absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl pointer-events-none group-focus-within:text-[#ff751f] transition-colors"></i>
                          </div>
                          {postcodeError && (
                            <p className="text-red-400 text-xs mt-2 flex items-center gap-1.5 bg-red-500/10 px-3 py-2.5 rounded-lg border border-red-500/20">
                              <i className="ri-error-warning-fill text-sm"></i>
                              {postcodeError}
                            </p>
                          )}
                          {isValidating && (
                            <p className="text-orange-400 text-xs mt-2 flex items-center gap-1.5 bg-orange-500/10 px-3 py-2.5 rounded-lg border border-orange-500/20">
                              <i className="ri-loader-4-line animate-spin text-sm"></i>
                              Validating postcode...
                            </p>
                          )}
                          {postcodeValid && (
                            <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1.5 bg-emerald-500/10 px-3 py-2.5 rounded-lg border border-emerald-500/20">
                              <i className="ri-checkbox-circle-fill text-sm"></i>
                              Postcode verified — we cover your area!
                            </p>
                          )}
                        </div>

                        {/* Step 2: Service Category */}
                        <div className="mb-8">
                          <div className="flex items-center gap-2.5 mb-3">
                            <span className="w-7 h-7 bg-gradient-to-br from-[#ff751f] to-orange-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-orange-500/30 ring-2 ring-orange-500/20">2</span>
                            <label className="text-white text-sm font-semibold">Select a category</label>
                          </div>
                          <div className="relative group">
                            <select
                              value={selectedCategory || ''}
                              onChange={(e) => setSelectedCategory(e.target.value)}
                              disabled={!postcodeValid}
                              className="w-full px-4 py-4 pr-12 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl text-white focus:outline-none focus:border-[#ff751f] focus:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed appearance-none transition-all duration-200 text-base font-medium cursor-pointer hover:border-white/30 disabled:hover:border-white/20"
                            >
                              <option value="" className="bg-gray-800 text-gray-300">Choose a service...</option>
                              <option value="handyman" className="bg-gray-800 text-white">🔧 Handyman</option>
                              <option value="plumber" className="bg-gray-800 text-white">🔧 Plumber</option>
                              <option value="electrician" className="bg-gray-800 text-white">⚡ Electrician</option>
                              <option value="cleaner" className="bg-gray-800 text-white">🧹 Cleaner</option>
                              <option value="waste-removal" className="bg-gray-800 text-white">🗑️ Waste Removal</option>
                              <option value="transportation" className="bg-gray-800 text-white">🚚 Transportation</option>
                            </select>
                            <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl pointer-events-none group-focus-within:text-[#ff751f] transition-colors"></i>
                          </div>
                        </div>

                        {/* Continue Button */}
                        <button
                          onClick={handleContinue}
                          disabled={!postcodeValid || !selectedCategory}
                          className="w-full bg-gradient-to-r from-[#ff751f] to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white font-bold py-4 px-6 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-orange-500/40 hover:shadow-xl hover:shadow-orange-500/50 flex items-center justify-center gap-2 text-base tracking-wide whitespace-nowrap hover:scale-[1.02] active:scale-[0.98] disabled:hover:scale-100"
                        >
                          {!postcodeValid || !selectedCategory ? (
                            <>
                              <i className="ri-lock-line text-lg"></i>
                              Complete steps above
                            </>
                          ) : (
                            <>
                              Continue to booking
                              <i className="ri-arrow-right-line text-lg"></i>
                            </>
                          )}
                        </button>

                        {/* Trust indicators */}
                        <div className="mt-6 pt-5 border-t border-white/10">
                          <div className="flex items-center justify-center gap-5 text-xs text-gray-400">
                            <div className="flex items-center gap-1.5">
                              <i className="ri-shield-check-fill text-emerald-400 text-base"></i>
                              <span className="font-medium">Verified Pros</span>
                            </div>
                            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                            <div className="flex items-center gap-1.5">
                              <i className="ri-price-tag-3-fill text-[#ff751f] text-base"></i>
                              <span className="font-medium">Fixed Pricing</span>
                            </div>
                            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
                            <div className="flex items-center gap-1.5">
                              <i className="ri-time-fill text-sky-400 text-base"></i>
                              <span className="font-medium">Same Day</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showServiceModal && (
          <ServiceModal
            isOpen={showServiceModal}
            onClose={() => setShowServiceModal(false)}
            category={selectedCategory}
            postcode={postcode}
          />
        )}
      </section>
    </div>
  );
}
